import { Disc, Mic, MicOff, Pause, Play, Square, Upload, X } from 'lucide-react';
import { useI18n } from '../../lib/i18n';
import { fmtElapsed } from '../../lib/share/peer';
import type { ControlCmd, ControlState } from '../../lib/rec/control';

/**
 * LA VENTANA DE CONTROL de una grabación (spec §4.12.1 punto 4): punto rojo, cronómetro,
 * Pause/Resume, Mute, Stop y Cancel (con confirmación), más "Recording: entire screen / window / tab".
 *
 * Es SOLO la pintura: la misma UI vive en tres lugares con distinto transporte —
 *  - dentro de la ventana Document PiP (Chromium, Firefox 151+), por un portal de React;
 *  - en el popup `/rec/control` (Safari, Firefox viejo), hablando por BroadcastChannel;
 *  - y en la píldora REC de la pestaña, por si el navegador bloqueó las dos anteriores.
 * Por eso recibe el `ControlState` y un `on(cmd)`, y no toca el provider.
 */
export function surfaceLabel(surface: ControlState['surface'], t: (k: string) => string): string {
  if (surface === 'monitor') return t('rec.surfaceMonitor');
  if (surface === 'window') return t('rec.surfaceWindow');
  if (surface === 'browser') return t('rec.surfaceBrowser');
  return t('rec.surfaceScreen');
}

export function RecordControlUI({ state, on, inWindow }: { state: ControlState; on: (cmd: ControlCmd) => void; inWindow?: boolean }) {
  const { t } = useI18n();
  const paused = state.phase === 'paused';
  const live = state.phase === 'recording' || paused;
  const cancel = () => { if (window.confirm(t('rec.cancelConfirm'))) on('cancel'); };
  return (
    <div className={`rec-ctl${inWindow ? ' in-window' : ''}`} role="group" aria-label={t('rec.controlTitle')}>
      <div className="rec-ctl-top">
        <span className={`rec-dot${paused ? ' paused' : ''}`} aria-hidden><Disc size={12} strokeWidth={3} /></span>
        <b className="rec-ctl-rec">{t('rec.rec')}</b>
        <span className="ss-mono rec-ctl-time">{state.phase === 'countdown' ? t('rec.countdown', { n: state.countdown }) : fmtElapsed(state.elapsedMs)}</span>
        {state.phase === 'starting' && <span className="rec-ctl-note">{t('share.connectingShort')}</span>}
        {state.uploadFailed && <span className="rec-ctl-note warn">{t('rec.uploadStuck')}</span>}
        {paused && state.pausedBy === 'net' && !state.uploadFailed && <span className="rec-ctl-note warn">{t('rec.pausedNet')}</span>}
        {paused && state.pausedBy === 'user' && <span className="rec-ctl-note">{t('rec.pausedLabel')}</span>}
        {state.limitSoon && !paused && <span className="rec-ctl-note warn">{t('rec.limitSoon')}</span>}
      </div>
      <div className="rec-ctl-label">{t('rec.recordingLabel', { surface: surfaceLabel(state.surface, t) })}</div>
      <div className="rec-ctl-actions">
        {live && (paused
          ? <button type="button" className="btn ghost sm" onClick={() => on('resume')} disabled={state.pausedBy === 'net'} title={state.pausedBy === 'net' ? t('rec.pausedNet') : undefined}><Play size={14} strokeWidth={2} /> {t('rec.resume')}</button>
          : <button type="button" className="btn ghost sm" onClick={() => on('pause')}><Pause size={14} strokeWidth={2} /> {t('rec.pause')}</button>)}
        {state.hasMic && live && (
          <button type="button" className={`btn ghost sm${state.muted ? ' is-muted' : ''}`} onClick={() => on(state.muted ? 'unmute' : 'mute')} aria-pressed={state.muted} title={state.muted ? t('share.unmuteMic') : t('share.mute')}>
            {state.muted ? <MicOff size={14} strokeWidth={2} /> : <Mic size={14} strokeWidth={2} />} {state.muted ? t('share.unmuteMic') : t('share.mute')}
          </button>
        )}
        {live && state.uploadFailed && <button type="button" className="btn sm" onClick={() => on('retry')} title={t('rec.uploadStuck')}><Upload size={13} strokeWidth={2} /> {t('rec.retryUpload')}</button>}
        {live && <button type="button" className="btn sm rec-stop" onClick={() => on('stop')}><Square size={13} strokeWidth={2.5} /> {t('rec.stop')}</button>}
        {(live || state.phase === 'countdown' || state.phase === 'starting') && (
          <button type="button" className="btn ghost sm rec-cancel" onClick={cancel} title={t('rec.cancel')}><X size={14} strokeWidth={2} /> {inWindow ? '' : t('rec.cancel')}</button>
        )}
      </div>
    </div>
  );
}
